/**
 * Keeping a Pickup Hungary session alive for up to 30 days.
 *
 * WHY THIS EXISTS
 * ---------------
 * An ID token from the dashboard pool lives ONE DAY — that is Cognito's
 * ceiling for one. The refresh token behind it lives thirty, but it is never
 * given to a browser: sign-in stores it on the server's Session row and hands
 * the client only the ID token. That is deliberate, and it is what makes
 * POST /user/logout able to really end a session — a client that has no
 * refresh token cannot keep one alive behind our back.
 *
 * So renewal is a round trip: POST /user/renew with the ID token we hold, and
 * the server finds its Session row, spends the stored refresh token and hands
 * back a fresh ID token. The row is the boundary; an invalidated one is never
 * renewed.
 *
 * MAKING IT CERTAIN, NOT LIKELY
 * -----------------------------
 * API Gateway's Cognito authorizer checks `exp` BEFORE any of our code runs,
 * so a stale token presented to an ordinary endpoint is a bare 401 from the
 * gateway — our Lambdas never hear about it. Renewal therefore cannot be left
 * to a timer that may not have fired (a laptop asleep, a tab backgrounded and
 * throttled). Two mechanisms, in this order:
 *
 *   1. BEFORE every request, `ensureFreshToken` renews if the token we are
 *      about to send expires inside RENEW_SKEW_MS. This is the one that
 *      matters: it is driven by the request itself, so there is no window in
 *      which a request goes out with a token we already knew was stale.
 *   2. A 401 still renews once and retries, which covers a clock that
 *      disagrees with Cognito's and a token whose `exp` we could not read.
 *
 * `/user/renew` itself is public precisely so that (2) can work at all.
 */
import axios from 'axios';
import { getToken, putToken } from './BearerToken';

/**
 * Renew when less than this is left. An hour out of a day: long enough that
 * a laptop waking from sleep still renews before its first request lands,
 * short enough that a session is not renewed on every page load.
 */
export const RENEW_SKEW_MS = 60 * 60 * 1000;

const buildBaseUrl = () => (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/**
 * Its own axios instance, on purpose. The default one carries API.js's
 * interceptors, which would attach the token we are replacing and — worse —
 * recurse into another renewal when this one 401s.
 */
export const renewClient = axios.create({
  baseURL: buildBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

/**
 * When this token expires, in epoch ms, or 0 when it cannot be read.
 *
 * The payload is decoded, NOT verified — this only schedules a renewal, and
 * a token we misread simply renews at the wrong moment, which the 401 path
 * then corrects. Nothing is trusted on the strength of this.
 */
export const expiryOf = (token) => {
  const payload = String(token || '').split('.')[1];
  if (!payload) return 0;

  try {
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      ),
    );
    return Number.isFinite(json?.exp) ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

/** True when the stored token is missing its life or close to the end of it. */
export const shouldRenewAhead = () => {
  const token = getToken();
  if (!token) return false;

  const expiresAt = expiryOf(token);
  // Unreadable expiry: leave it alone and let a 401 drive the renewal,
  // rather than renewing on every single request.
  if (!expiresAt) return false;

  return expiresAt - Date.now() <= RENEW_SKEW_MS;
};

// --- session-ended notification -------------------------------------------
const endedListeners = new Set();

export const onSessionEnded = (listener) => {
  endedListeners.add(listener);
  return () => endedListeners.delete(listener);
};

const notifySessionEnded = () => {
  endedListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A broken listener must not break the request pipeline.
    }
  });
};

export const clearSession = () => {
  try {
    window.localStorage.removeItem('SESSION_TOKEN');
  } catch {
    // Private mode: nothing to clear, and nothing to do about it.
  }
};

const endSession = () => {
  clearSession();
  notifySessionEnded();
};

/** A 4xx is the server saying no. Offline and 5xx say nothing about the session. */
const isRefusal = (error) => {
  const status = error?.response?.status;
  return status >= 400 && status < 500;
};

let renewInFlight = null;

/**
 * Swap the token we hold for a fresh one. Concurrent callers share a single
 * request, so a screen firing three calls at once renews once — which matters
 * more than usual here, because the server re-points the Session row at the
 * new token and a second renewal with the OLD one would be refused.
 */
export const renewSession = () => {
  if (renewInFlight) return renewInFlight;

  const presented = getToken();
  if (!presented) return Promise.reject(new Error('No session to renew'));

  renewInFlight = renewClient
    .post('/user/renew', { id_token: presented })
    .then((response) => {
      const payload = response?.data?.data ?? response?.data;
      const idToken = payload?.idToken;
      if (!idToken) throw new Error('Renewal returned no token');
      putToken(idToken);
      return idToken;
    })
    .catch((error) => {
      // Another TAB may have renewed while this request was in flight. The
      // server re-points the row, so our request was refused for a token
      // that is simply no longer current — not because the session ended.
      // Storage already holds the good one; use it.
      const current = getToken();
      if (current && current !== presented) return current;
      throw error;
    })
    .finally(() => {
      renewInFlight = null;
    });

  return renewInFlight;
};

/**
 * The token to send, renewed first if it is close to expiry. Resolves to a
 * usable token, or null when the session could not be kept alive — only a
 * refusal ends it, so offline simply tries again on the next request.
 */
export const ensureFreshToken = async () => {
  if (!shouldRenewAhead()) return getToken();

  try {
    return await renewSession();
  } catch (error) {
    if (isRefusal(error)) endSession();
    return getToken();
  }
};

/** Test seam — drops any in-flight renewal so specs start from a clean slate. */
export const resetRenewalState = () => {
  renewInFlight = null;
};

export { endSession, isRefusal };
